import { useState, type FormEvent, type ReactNode } from 'react';

export interface ResetPasswordViewProps {
  /**
   * Saves the new password on the recovery session; resolves to an Estonian
   * error message, or null on success (the caller then re-enters the app).
   */
  onSubmit: (password: string) => Promise<string | null>;
}

/**
 * The new-password screen shown when the app is opened through a Supabase
 * recovery link (src/auth/useAuth flags the recovery session). Presentation
 * only — the Supabase updateUser call arrives via `onSubmit`. Validates the
 * confirmation match locally so a typo never reaches Supabase.
 */
export function ResetPasswordView({ onSubmit }: ResetPasswordViewProps): ReactNode {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (pending) return;
    if (password !== confirm) {
      setError('Paroolid ei kattu.');
      return;
    }
    setPending(true);
    setError(null);
    const message = await onSubmit(password);
    setPending(false);
    if (message) setError(message);
  };

  return (
    <div className="flex h-screen items-center justify-center bg-background text-foreground">
      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="w-full max-w-sm space-y-4 rounded-lg border border-border bg-card p-6"
        aria-label="Uue parooli määramine"
      >
        <div>
          <h1 className="text-base font-semibold tracking-tight">Progear Finance</h1>
          <p className="mt-1 text-xs text-muted-foreground">Määra oma kontole uus parool</p>
        </div>

        {error ? (
          <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </p>
        ) : null}

        <label className="block text-xs">
          <span className="mb-1 block text-muted-foreground">Uus parool</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-ring"
          />
        </label>

        <label className="block text-xs">
          <span className="mb-1 block text-muted-foreground">Korda uut parooli</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={6}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-ring"
          />
        </label>

        <button
          type="submit"
          disabled={pending}
          className="h-9 w-full rounded-md bg-primary text-sm font-medium text-primary-foreground transition-opacity disabled:opacity-60"
        >
          {pending ? 'Salvestan…' : 'Salvesta uus parool'}
        </button>
      </form>
    </div>
  );
}
